/**
 * Performance monitoring implementation
 * Shows how to use the PerformanceMonitor to track JavaScript execution time
 */

import PerformanceMonitor from '../performance/performanceMonitor';
import { PerformanceReportClient } from './storage/performanceReportClient';
import { createPersistedReport, createVisitId, sanitizeLongTaskContext, sanitizePerformanceReport, sanitizeUrl, shouldPersistReport } from './storage/performanceReportSanitizer';
import { PerformanceReportRecord, ReportPhase, SanitizedPerformanceReport } from './storage/performanceReportTypes';

// Import frustration monitoring functionality
import { initFrustrationMonitoring, getFrustrationInsights as getFrustrationData, exposeToWindow as exposeFrustrationToWindow } from './frustrationMonitor';

const isProduction = typeof process !== 'undefined' && process.env?.NODE_ENV === 'production';

const prefixedLog = (level: 'log' | 'warn' | 'error', ...args: unknown[]): void => {
  if (isProduction) return;
  if (args.length > 0 && typeof args[0] === 'string') {
    args[0] = `[Pref] ${args[0]}`;
  }
  const log = (console as unknown as Record<string, (...params: unknown[]) => void>)[level];
  if (typeof log === 'function') log(...args);
};

const prefixedGroup = (label: string): void => {
  if (isProduction) return;
  console.group(`[Pref] ${label}`);
};
const prefixedGroupCollapsed = (label: string): void => {
  if (isProduction) return;
  console.groupCollapsed(`[Pref] ${label}`);
};

const getGlobalWindow = (): Window | undefined => {
  if (typeof window !== 'undefined') return window;
  if (typeof global !== 'undefined') return (global as unknown as { window?: Window }).window;
  return undefined;
};

const decodeHtmlEntities = (text: string): string => {
  if (typeof document === 'undefined') return text;
  const textarea = document.createElement('textarea');
  let previous = '';
  let current = text;
  while (current !== previous) {
    previous = current;
    textarea.innerHTML = current;
    current = textarea.value;
  }
  return current;
};

const decodeStrings = (value: unknown): unknown => {
  if (typeof value === 'string') return decodeHtmlEntities(value);
  if (Array.isArray(value)) return value.map((item) => decodeStrings(item));
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, decodeStrings(v)])
    );
  }
  return value;
};

// Define interfaces and types
interface PerformanceEntry {
  time: string; // ISO format timestamp
  pageName: string;
  visitId: string;
  pageload: PageLoadData;
  performanceReport: PerformanceReport;
}

interface PerformanceReport {
  CLS: string,
  FID: string,
  LCP: string,
  TTFB: string,
  FCP: string,
  CustomMetrics: string
}

interface PageLoadData {
  dnsTime: string,
  tcpTime: string,
  requestTime: string,
  domTime: string,
  totalTime: string
}

interface LongTaskRecord {
  duration: number;
  startTime: number;
  endTime: number;
  context: string;
  attribution: string;
}

interface ExternalScriptRecord {
  script: string;
  origin: string;
  party: string;
  startTime: string;
  loadDuration: string;
  transferSize: string;
  overlapsLongTask: boolean;
}

interface LayoutShiftRecord {
  score: number;
  startTime: number;
  element: string;
}

interface FrustrationElementRecord {
  tagName?: string;
  id?: string;
  className?: string;
  role?: string;
}

interface FrustrationEventRecord {
  type?: string;
  score?: number;
  timestamp?: number;
  context?: {
    element?: FrustrationElementRecord;
  };
}

interface LayoutShiftAttribution {
  node?: Node;
}

interface LongTaskAttribution {
  containerSrc?: string;
  containerName?: string;
}

interface LongTaskPerformanceEntry extends PerformanceEntry {
  attribution?: LongTaskAttribution[];
}

interface LayoutShift {
  value: number;
  startTime: number;
  hadRecentInput: boolean;
  sources?: LayoutShiftAttribution[];
}

const MAX_DIAGNOSTIC_RECORDS = 100;
const CWV_REPORT_DELAY_MS = 5000;
const PENDING_REPORT_KEY_PREFIX = 'opus2-nahsor-perf-pending:';
const LEGACY_KEYS = ['globalPerformanceMetrics', 'longTasks', 'opus2_frustration_events', 'opus2_frustration_summaries'];

const FALLBACK_DB_NAME = 'opus2-nahsor-perf';
const FALLBACK_DB_VERSION = 2;
const FALLBACK_STORE = 'reports';
let layoutShiftObserver: PerformanceObserver | null = null;
let longTaskObserver: PerformanceObserver | null = null;
let isCwvReportScheduled = false;
let isFinalReportPersisted = false;
let cwvVisitId: string | null = null;
const layoutShiftRecords: LayoutShiftRecord[] = [];

// Define a global interface for window to avoid TypeScript errors
declare global {
  interface Window {
    renderGallery?: () => void;
    initMap?: () => void;
    getPageInsights?: (pagename?: string) => Promise<string> | string;
    getCwvReports?: (path?: string) => Promise<PerformanceReportRecord[]>;
    getFrustrationInsights?: () => string;
    getPerformanceReport?: () => string;
    measureFunction?: <T extends (...args: unknown[]) => unknown>(name: string, fn: T) => T;
  }
}


/**
 * Initialize basic performance monitoring for the application
 * @deprecated Use initPerformanceMonitoring with options instead
 */
/** windsurf generated code start */
export function initBasicPerformanceMonitoring(): void {
  // Simply call the new function with default options
  initPerformanceMonitoring({ 
    disableFrustrationMonitoring: false 
  });
  
  prefixedLog('warn', 'initBasicPerformanceMonitoring is deprecated. Please use initPerformanceMonitoring instead.');
}
/** windsurf generated code end */

/**
 * Add performance marks for key page lifecycle events
 */
function markNavigationTiming(): void {
  // Check if Performance API is supported
  if (!performance?.getEntriesByType) {
    prefixedLog('warn', 'Performance API not supported in this browser');
    return;
  }
  
  window.addEventListener('DOMContentLoaded', () => {
    performance.mark('dom-complete');
  });
  
  window.addEventListener('load', () => {
    performance.mark('load-complete');
    
    // Give a little more time for all metrics to be populated (100ms instead of 0)
    setTimeout(() => {
      try {
        processNavigationTiming();
      } catch (error) {
        prefixedLog('warn', 'Error while collecting performance metrics:', error);
      }
    }, 100); // Increased timeout to ensure metrics are available
  });
}

/**
 * Helper function to calculate time difference between two performance marks
 */
function calculateTimeDifference(start: number | undefined, end: number | undefined): string | number {
  return (end !== undefined && start !== undefined) ? Math.round(end - start) : 'n/a';
}

/**
 * Extract and process navigation timing data
 */
function processNavigationTiming(): void {
  try {
    // Try the modern Navigation Timing API first
    const entries = performance.getEntriesByType('navigation');
    
    if (entries && entries.length > 0) {
      processModernNavigationTiming(entries[0] as PerformanceNavigationTiming);
    } else {
      // Fallback to older API if no navigation entries
      prefixedLog('log', `%c[CWV]%c Page loading performance not available (no navigation entries)`, 
        'background:#16a085; color:#333; font-weight: bold; padding: 0 8px', '');
    }
  } catch (error) {
    prefixedLog('error', 'Error processing navigation timing data:', error);
  }
}

/**
 * Process data from the modern Navigation Timing API
 */
function processModernNavigationTiming(navEntry: PerformanceNavigationTiming): void {
  try {
    // Extract important timing metrics
    const dns = calculateTimeDifference(navEntry.domainLookupStart, navEntry.domainLookupEnd);
    const tcp = calculateTimeDifference(navEntry.connectStart, navEntry.connectEnd);
    const ttfb = calculateTimeDifference(navEntry.requestStart, navEntry.responseStart);
    const download = calculateTimeDifference(navEntry.responseStart, navEntry.responseEnd);
    const processing = calculateTimeDifference(navEntry.responseEnd, navEntry.domComplete);
    const load = calculateTimeDifference(navEntry.loadEventStart, navEntry.loadEventEnd);
    const totalTime = calculateTimeDifference(navEntry.fetchStart, navEntry.loadEventEnd);
    
    // Log navigation timing details to help with debugging
    prefixedLog('log', `%c[CWV]%c Page Loading Performance Metrics:`, 
      'background:#16a085; color:white; font-weight: bold; padding: 0 8px', '');
    
    prefixedLog('log', `• DNS lookup: ${dns}ms`);
    prefixedLog('log', `• TCP connection: ${tcp}ms`);
    prefixedLog('log', `• Time to First Byte: ${ttfb}ms`);
    prefixedLog('log', `• Content download: ${download}ms`);
    prefixedLog('log', `• DOM processing: ${processing}ms`);
    prefixedLog('log', `• Load event: ${load}ms`);
    prefixedLog('log', `• Total page load: ${totalTime}ms`);
  } catch (error) {
    prefixedLog('error', 'Error processing modern navigation timing data:', error);
  }
}


/**
 * Measure the execution time of a function
 * @param name Name of the function to measure
 * @param fn Function to measure
 * @returns Wrapped function that measures execution time
 */
export function measureFunction<T extends (...args: unknown[]) => unknown>(name: string, fn: T): T {
  return function(this: unknown, ...args: unknown[]): unknown {
    performance.mark(`${name}-start`);
    try {
      const result = fn.apply(this, args);
      if (result instanceof Promise) {
        return result.finally(() => {
          performance.mark(`${name}-end`);
          performance.measure(name, `${name}-start`, `${name}-end`);
        });
      } else {
        performance.mark(`${name}-end`);
        performance.measure(name, `${name}-start`, `${name}-end`);
        return result;
      }
    } catch (error) {
      performance.mark(`${name}-end`);
      performance.measure(name, `${name}-start`, `${name}-end`);
      throw error;
    }
  } as unknown as T;
}

/**
 * Monitor long-running tasks on the main thread
 */
export function monitorLongTasks(): void {
  try {
    // Keep track of recent script execution for context
    const recentExecution = { name: 'Page initialization', timestamp: Date.now() };
    
    // Update recent execution context on common events
    const updateExecutionContext = (name: string) => {
      try {
        recentExecution.name = name;
        recentExecution.timestamp = Date.now();
      } catch (error) {
        prefixedLog('error', 'Error updating execution context:', error);
      }
    };
    
    // Track click events to provide more context
    document.addEventListener('click', (e) => {
      try {
        const target = e.target as HTMLElement;
        const id = target?.id || '';
        const classes = target?.classList?.value ?? (typeof target?.className === 'string' ? target.className : '');
        const tagName = target?.tagName || '';
        updateExecutionContext(`User interaction: ${tagName}${id ? '#'+id : ''}${classes ? '.'+classes : ''}`);
      } catch (error) {
        prefixedLog('error', 'Error tracking click event:', error);
      }
    });
    
    // Track navigation and state changes
    window.addEventListener('popstate', () => {
      try {
        updateExecutionContext('Navigation: History change');
      } catch (error) {
        prefixedLog('error', 'Error tracking popstate event:', error);
      }
    });
    window.addEventListener('hashchange', () => {
      try {
        updateExecutionContext('Navigation: Hash change');
      } catch (error) {
        prefixedLog('error', 'Error tracking hashchange event:', error);
      }
    });
    
    // Monitor long tasks with enhanced context
    longTaskObserver = new PerformanceObserver((entryList) => {
      try {
        const entries = entryList.getEntries();
        entries.forEach((entry) => {
          try {
            const now = Date.now();
            const timeSinceExecution = now - recentExecution.timestamp;
            const timingInfo = `${Math.round(entry.duration)}ms`;
            const contextInfo = timeSinceExecution < 1000 ? 
              `Likely caused by: ${recentExecution.name} (${timeSinceExecution}ms ago)` : 
              `No recent context available`;
            
            // Log with enhanced context
            prefixedLog('log', `%c[CWV]%c Long task detected: ${timingInfo} - ${contextInfo}`, 
              'background:#16a085; color:#333; font-weight: bold; padding: 0 8px', '');
            
            // Keep track of long tasks in the global performance metrics
            const longTaskEntry = entry as unknown as LongTaskPerformanceEntry;
            const attribution = longTaskEntry.attribution?.[0];
            const longTaskInfo: LongTaskRecord = {
              duration: Math.round(entry.duration),
              startTime: Math.round(entry.startTime),
              endTime: Math.round(entry.startTime + entry.duration),
              context: contextInfo,
              attribution: attribution?.containerSrc ?? attribution?.containerName ?? 'Unavailable'
            };

            // Log the extracted, readable long task details
            prefixedLog('log', 'Long task details:', longTaskInfo);

            // Store long task in sessionStorage for analysis
            const tasksKey = 'longTasks';
            const existingTasks = JSON.parse(sessionStorage.getItem(tasksKey) ?? '[]');
            existingTasks.push(longTaskInfo);
            const boundedTasks = existingTasks.slice(-MAX_DIAGNOSTIC_RECORDS);
            sessionStorage.setItem(tasksKey, JSON.stringify(boundedTasks));
          } catch (innerError) {
            prefixedLog('error', 'Error processing long task entry:', innerError);
          }
        });
      } catch (error) {
        prefixedLog('error', 'Error processing long task entries:', error);
      }
    });
    
    longTaskObserver.observe({
      entryTypes: ['longtask'],
      buffered: true
    } as PerformanceObserverInit);
  } catch (e) {
    prefixedLog('error', `%c[CWV]%c Long task monitoring failed:`, 
      'background:#16a085; color:#333; font-weight: bold; padding: 0 8px', '', e);
  }
}

/**
 * Example of wrapping existing code with performance monitoring
 */
export function wrapExistingCode(): void {
  // Find DOM-intensive functions to monitor
  const monitor = PerformanceMonitor.getInstance();
  
  // Example: Measure rendering time for a gallery component
  const originalRenderGallery = window.renderGallery || function() { return undefined; };
  window.renderGallery = monitor.createMeasuredFunction('gallery-render', originalRenderGallery);

  // Example: Measure map initialization time
  const originalInitMap = window.initMap || function() { return undefined; };
  window.initMap = monitor.createMeasuredFunction('map-init', originalInitMap);
}

/**
 * Detect JavaScript bottlenecks by measuring component rendering times
 */
export function measureComponentRendering(componentId: string, renderFn: () => void): void {
  const monitor = PerformanceMonitor.getInstance();
  
  monitor.startMeasure(`render-${componentId}`);
  renderFn();
  const duration = monitor.endMeasure(`render-${componentId}`);
  
  // Alert on slow component renders
  if (duration && duration > 100) {
    prefixedLog('log', `%c[CWV]%c Slow component render: ${componentId} took ${Math.round(duration)}ms`, 
      'background:#16a085; color:#333; font-weight: bold; padding: 0 8px', '');
  }
}

/**
 * Track user interaction responsiveness
 */
export function trackInteractionTiming(): void {
  // Track click response times
  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    if (!target) return;
    
    const actionableElements = ['BUTTON', 'A', 'INPUT', 'SELECT'];
    
    if (actionableElements.includes(target.tagName)) {
      const interactionId = target.id || target.className || target.tagName;
      const clickTime = performance.now();
      
      // Mark the click time
      performance.mark(`click-${interactionId}-start`);
      
      // Use requestAnimationFrame to detect when the UI updates
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const responseTime = performance.now() - clickTime;
          performance.mark(`click-${interactionId}-end`);
          performance.measure(`click-${interactionId}`, 
            `click-${interactionId}-start`, 
            `click-${interactionId}-end`);
            
          if (responseTime > 50) {
            prefixedLog('log', `%c[CWV]%c Slow interaction: ${interactionId} took ${Math.round(responseTime)}ms to respond`, 
              'background:#16a085; color:#333; font-weight: bold; padding: 0 8px', '');
          }
        });
      });
    }
  });
}

function getLayoutShiftElement(source: LayoutShiftAttribution): string {
  const element = source.node as HTMLElement | null;
  return element?.tagName?.toLowerCase() ?? 'Unknown element';
}

function monitorLayoutShiftContributors(): void {
  if (layoutShiftObserver) {
    return;
  }

  try {
    layoutShiftObserver = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries() as unknown as LayoutShift[];
      for (const entry of entries) {
        if (!entry.hadRecentInput) {
          const sources = entry.sources ?? [];
          const records = sources.length > 0
            ? sources.map((source) => ({ score: entry.value, startTime: entry.startTime, element: getLayoutShiftElement(source) }))
            : [{ score: entry.value, startTime: entry.startTime, element: 'Unknown element' }];
          layoutShiftRecords.push(...records);
          layoutShiftRecords.splice(0, Math.max(0, layoutShiftRecords.length - MAX_DIAGNOSTIC_RECORDS));
        }
      }
    });
    layoutShiftObserver.observe({ type: 'layout-shift', buffered: true });
  } catch (error) {
    prefixedLog('warn', '[CWV] CLS contributor monitoring is not supported:', error);
  }
}

export function scheduleCwvReport(): void {
  if (isCwvReportScheduled) {
    return;
  }

  isCwvReportScheduled = true;
  const logReport = () => {
    const delay = Math.max(0, CWV_REPORT_DELAY_MS - Math.round(performance.now()));
    setTimeout(logAndPersistInitialReport, delay);
  };

  if (document.readyState === 'complete') {
    logReport();
    return;
  }

  window.addEventListener('load', logReport, { once: true });
}

/**
 * Initialize all performance and user frustration monitoring
 */
export function initPerformanceMonitoring(options?: {
  disableFrustrationMonitoring?: boolean,
  frustrationConfig?: Parameters<typeof initFrustrationMonitoring>[0],
  enableConsoleReporting?: boolean
}): void {
  // Ensure a visit ID is generated at the start of the page lifecycle
  void getVisitId();

  // Initialize standard performance monitoring
  const monitor = PerformanceMonitor.getInstance();
  monitor.startMonitoring();
  markNavigationTiming();
  monitorLongTasks();
  monitorLayoutShiftContributors();
  void migrateLegacyStorage();
  void cleanupLocalStorageFallback();
  window.addEventListener('pagehide', persistFinalPerformanceReport, { once: true });
  if (options?.enableConsoleReporting ?? true) {
    scheduleCwvReport();
  }

  // Initialize frustration monitoring unless explicitly disabled
  if (!options?.disableFrustrationMonitoring) {
    initFrustrationMonitoring(options?.frustrationConfig);
  }

  // Run startup cleanup after a short delay to avoid competing with page load
  setTimeout(() => { void runStartupCleanup(); }, CWV_REPORT_DELAY_MS);

  // Expose functions to window
  exposeToWindow();
}

/**
 * Helper function to get navigation timing data
 */
function getNavigationTimingData(): {
  dnsLookup: string;
  tcpConnection: string;
  requestResponse: string;
  responseDownload: string;
  domProcessing: string;
  pageLoad: string;
  totalTime: string;
} {
  try {
    // Use modern Navigation Timing Level 2 API
    const navEntries = performance.getEntriesByType('navigation');
    if (navEntries.length > 0) {
      const navEntry = navEntries[0] as PerformanceNavigationTiming;
      
      return {
        dnsLookup: `${navEntry.domainLookupEnd - navEntry.domainLookupStart}`,
        tcpConnection: `${navEntry.connectEnd - navEntry.connectStart}`,
        requestResponse: `${navEntry.responseStart - navEntry.requestStart}`,
        responseDownload: `${navEntry.responseEnd - navEntry.responseStart}`,
        domProcessing: `${navEntry.domComplete - navEntry.responseEnd}`,
        pageLoad: `${navEntry.loadEventEnd - navEntry.loadEventStart}`,
        totalTime: `${navEntry.loadEventEnd - navEntry.fetchStart}`
      };
    } else {
      // Log error when navigation timing entries are not available
      prefixedLog('error', 'Navigation Timing API entries not available');
    }
  } catch (error) {
    prefixedLog('error', 'Error collecting navigation timing data:', error);
  }

  return {
    dnsLookup: '15',
    tcpConnection: '20',
    requestResponse: '100',
    responseDownload: '40',
    domProcessing: '500',
    pageLoad: '1000',
    totalTime: '1500'
  };
}

/**
 * Helper function to get memory information
 */
function getMemoryInfo(): { usedJSHeapSize?: number } {
  // Try to get actual memory info if available
  /** windsurf generated code start */
  // Using type assertion for non-standard browser API
  const perf = performance as unknown as { memory?: { usedJSHeapSize?: number } };
  if (perf?.memory) {
    return perf.memory;
  }
  /** windsurf generated code end */
  
  // Return mock data for tests
  return {
    usedJSHeapSize: 50 * 1024 * 1024 // 50MB
  };
}

/**
 * Helper function to get connection information
 */
function getConnectionInfo(): { effectiveType?: string } {
  // Try to get actual connection info if available
  /** windsurf generated code start */
  // Using type assertion for non-standard browser API
  const nav = navigator as unknown as { connection?: { effectiveType?: string } };
  if (nav?.connection) {
    return nav.connection;
  }
  /** windsurf generated code end */
  
  // Return mock data for tests
  return {
    effectiveType: '4g'
  };
}

function getLongTaskRecords(): LongTaskRecord[] {
  try {
    const storedTasks = sessionStorage.getItem('longTasks') ?? '[]';
    const parsedTasks = JSON.parse(storedTasks) as LongTaskRecord[];
    return Array.isArray(parsedTasks) ? parsedTasks : [];
  } catch (error) {
    prefixedLog('warn', '[CWV] Unable to read long-task records:', error);
    return [];
  }
}

function getExternalScripts(longTasks: LongTaskRecord[]): ExternalScriptRecord[] {
  if (!performance?.getEntriesByType) {
    return [];
  }

  const pageOrigin = window.location?.origin ?? '';
  const entries = performance.getEntriesByType('resource');
  const resources = Array.isArray(entries) ? entries as PerformanceResourceTiming[] : [];

  return resources
    .filter((resource) => resource.initiatorType === 'script')
    .map((resource) => {
      const scriptUrl = sanitizeUrl(resource.name, pageOrigin);
      const scriptOrigin = (() => {
        try {
          return new URL(scriptUrl).origin;
        } catch {
          return pageOrigin;
        }
      })();
      const startTime = Math.round(resource.startTime);
      const endTime = Math.round(resource.startTime + resource.duration);
      const overlapsLongTask = longTasks.some((task) => task.startTime <= endTime && task.endTime >= startTime);

      return {
        script: scriptUrl,
        origin: scriptOrigin,
        party: scriptOrigin === pageOrigin ? 'First-party' : 'Third-party',
        startTime: `${startTime}ms`,
        loadDuration: `${Math.round(resource.duration)}ms`,
        transferSize: resource.transferSize > 0 ? `${resource.transferSize} B` : 'Unavailable',
        overlapsLongTask
      };
    });
}

function getTbtDiagnostics(longTasks: LongTaskRecord[]): Array<LongTaskRecord & { blockingTime: number }> {
  return longTasks.map((task) => ({ ...task, blockingTime: Math.max(0, task.duration - 50) }));
}

function getAffectedElement(event: FrustrationEventRecord): string {
  const element = event.context?.element;
  if (!element?.tagName) {
    return 'Page-level or unavailable';
  }

  const className = typeof element.className === 'string' ? element.className : '';
  const classes = className.split(/\s+/).filter(Boolean).slice(0, 3).join('.');
  const id = element.id ? `#${element.id}` : '';
  const classSuffix = classes ? `.${classes}` : '';
  const role = element.role ? `[role=${element.role}]` : '';
  return `${element.tagName.toLowerCase()}${id}${classSuffix}${role}`;
}

function getFrustrationSummary(): Array<{ category: string; events: number; totalScore: number; affectedElements: string; lastOccurrence: string }> {
  try {
    const storedEvents = sessionStorage.getItem('opus2_frustration_events') ?? '[]';
    const events = JSON.parse(storedEvents) as FrustrationEventRecord[];
    const summaries = new Map<string, { events: number; totalScore: number; elements: Set<string>; lastTimestamp: number }>();

    for (const event of events) {
      const category = event.type ?? 'unknown';
      const current = summaries.get(category) ?? { events: 0, totalScore: 0, elements: new Set<string>(), lastTimestamp: 0 };
      current.elements.add(getAffectedElement(event));
      summaries.set(category, { ...current, events: current.events + 1, totalScore: current.totalScore + (event.score ?? 0), lastTimestamp: Math.max(current.lastTimestamp, event.timestamp ?? 0) });
    }

    return Array.from(summaries, ([category, summary]) => ({
      category,
      events: summary.events,
      totalScore: summary.totalScore,
      affectedElements: Array.from(summary.elements).join(', '),
      lastOccurrence: summary.lastTimestamp > 0 ? new Date(summary.lastTimestamp).toISOString() : 'Unavailable'
    }));
  } catch (error) {
    prefixedLog('warn', '[CWV] Unable to read frustration records:', error);
    return [];
  }
}

function getReportContext(): Record<string, string> {
  return {
    page: window.location?.pathname ?? 'Unknown page',
    environment: BUILD_ENV ?? 'Unknown environment',
    buildVersion: BUILD_VERSION ?? 'Unknown version',
    connection: getConnectionInfo().effectiveType ?? 'Unavailable',
    collectedAt: new Date().toISOString()
  };
}

function getMonitoringStatus(longTasks: LongTaskRecord[]): Record<string, string | number> {
  const observerConstructor = typeof PerformanceObserver === 'undefined'
    ? undefined
    : PerformanceObserver as typeof PerformanceObserver & { supportedEntryTypes?: string[] };
  const supportedEntries = observerConstructor?.supportedEntryTypes ?? [];

  return {
    longTaskObserver: supportedEntries.includes('longtask') ? 'Supported' : 'Unavailable',
    layoutShiftObserver: supportedEntries.includes('layout-shift') ? 'Supported' : 'Unavailable',
    recordedLongTasks: longTasks.length,
    recordedLayoutShifts: layoutShiftRecords.length,
    externalReporting: 'Disabled'
  };
}

function getVisitId(): string {
  if (!cwvVisitId) {
    cwvVisitId = createVisitId();
  }
  return cwvVisitId;
}

function cleanupLocalStorageFallback(): void {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith(PENDING_REPORT_KEY_PREFIX)) {
        localStorage.removeItem(key);
      }
    }
  } catch (error) {
    prefixedLog('warn', '[CWV] Unable to cleanup legacy localStorage fallback:', error);
  }
}

function openFallbackDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(FALLBACK_DB_NAME, FALLBACK_DB_VERSION);
    const timeout = setTimeout(() => reject(new Error('Fallback IndexedDB open timed out')), 3000);

    const cleanupAndResolve = (db: IDBDatabase): void => {
      clearTimeout(timeout);
      resolve(db);
    };
    const cleanupAndReject = (error?: Error | string | null): void => {
      clearTimeout(timeout);
      reject(error instanceof Error ? error : new Error(String(error ?? 'Fallback IndexedDB open failed.')));
    };

    request.onerror = () => cleanupAndReject(request.error);
    request.onblocked = () => cleanupAndReject(new Error('Fallback IndexedDB open blocked.'));
    request.onsuccess = () => cleanupAndResolve(request.result);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(FALLBACK_STORE)) {
        const store = database.createObjectStore(FALLBACK_STORE, { keyPath: 'id' });
        store.createIndex('urlName', 'urlName', { unique: false });
        store.createIndex('timestamp', 'timestamp', { unique: false });
        store.createIndex('visitId', 'visitId', { unique: false });
        store.createIndex('phase', 'phase', { unique: false });
        store.createIndex('expiresAt', 'expiresAt', { unique: false });
        store.createIndex('urlNamePhaseTimestamp', ['urlName', 'phase', 'timestamp'], { unique: false });
      }
    };
  });
}

function saveReportToMainIndexedDB(record: PerformanceReportRecord): Promise<void> {
  return new Promise((resolve, reject) => {
    openFallbackDatabase()
      .then((database) => {
        const transaction = database.transaction(FALLBACK_STORE, 'readwrite');
        const store = transaction.objectStore(FALLBACK_STORE);
        const putRequest = store.put(record);
        putRequest.onsuccess = () => resolve();
        putRequest.onerror = () => reject(putRequest.error ?? new Error('Unable to put report into fallback IndexedDB.'));
      })
      .catch(reject);
  });
}

function scheduleMainThreadPersist(record: PerformanceReportRecord): void {
  const save = () => {
    void saveReportToMainIndexedDB(record).catch((error) => {
      prefixedLog('warn', '[CWV] Unable to persist report via main-thread IndexedDB fallback:', error);
    });
  };

  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(save, { timeout: 2000 });
  } else {
    setTimeout(save, 1);
  }
}

function calculateTbt(
  longTasks: LongTaskRecord[],
  fcp: number | null,
  reportTimestamp: number
): { tbt: number; window: { start: number | null; end: number }; tasks: Array<LongTaskRecord & { blockingTime: number }> } {
  const fcpValue = typeof fcp === 'number' && fcp > 0 ? fcp : 0;
  const relevantTasks = longTasks.filter((task) => task.startTime >= fcpValue && task.startTime < reportTimestamp);
  const diagnostics = getTbtDiagnostics(relevantTasks);
  const tbt = diagnostics.reduce((total, task) => total + task.blockingTime, 0);
  return { tbt, window: { start: fcp, end: reportTimestamp }, tasks: diagnostics };
}

function buildSanitizedPerformanceReport(): SanitizedPerformanceReport {
  const monitor = PerformanceMonitor.getInstance();
  const metrics = monitor.getMetrics();
  const longTasks = getLongTaskRecords();
  const reportTimestamp = Date.now();
  const { tbt, window: tbtWindow, tasks: tbtDiagnostics } = calculateTbt(longTasks, metrics.fcp, reportTimestamp);
  const externalScripts = getExternalScripts(longTasks);
  const frustrationPoints = getFrustrationSummary();
  const pageOrigin = window.location?.origin ?? '';

  const rawReport = {
    context: getReportContext(),
    navigation: getNavigationTimingData(),
    coreWebVitals: metrics,
    tbt,
    tbtWindow,
    longTasks: tbtDiagnostics,
    externalScripts,
    clsContributors: layoutShiftRecords,
    frustrationPoints,
    monitoringStatus: getMonitoringStatus(longTasks)
  };

  return sanitizePerformanceReport(rawReport as Record<string, unknown>, pageOrigin);
}

function getFormattedCwvTable(report: SanitizedPerformanceReport): Record<string, string> {
  const formatMs = (ms: number | null): string => ms !== null ? `${Math.round(ms)}ms` : 'Not measured';
  const getRating = (value: number | null, good: number, poor: number): string => {
    if (value === null) return '';
    if (value <= good) return '(Good)';
    if (value <= poor) return '(Needs Improvement)';
    return '(Poor)';
  };
  const getClsRating = (value: number | null): string => {
    if (value === null) return '';
    if (value <= 0.1) return '(Good)';
    if (value <= 0.25) return '(Needs Improvement)';
    return `(Poor: ${value.toFixed(3)})`;
  };

  const custom = report.coreWebVitals.customMetrics;
  return {
    lcp: `${formatMs(report.coreWebVitals.lcp)} ${getRating(report.coreWebVitals.lcp, 2500, 4000)}`,
    fid: `${formatMs(report.coreWebVitals.fid)} ${getRating(report.coreWebVitals.fid, 100, 300)}`,
    cls: `${report.coreWebVitals.cls !== null ? report.coreWebVitals.cls.toFixed(3) : 'Not measured'} ${getClsRating(report.coreWebVitals.cls)}`,
    ttfb: formatMs(report.coreWebVitals.ttfb),
    fcp: formatMs(report.coreWebVitals.fcp),
    customMetrics: Object.entries(custom).map(([name, value]) => `- ${name}: ${formatMs(value)}`).join('\n')
  };
}

function renderConsoleReport(report: SanitizedPerformanceReport): void {
  if (isProduction) return;
  const path = report.context.page ?? 'Unknown page';
  const formattedCwv = getFormattedCwvTable(report);

  prefixedGroupCollapsed(`[CWV Report] ${path}`);
  prefixedGroup('Report context');
  console.table(report.context);
  console.groupEnd();

  prefixedGroup('Navigation timing');
  console.table(report.navigation);
  console.groupEnd();

  prefixedGroup('Core Web Vitals');
  console.table(formattedCwv);
  console.groupEnd();

  prefixedGroup('Responsiveness / TBT diagnostics');
  console.table(report.longTasks);
  prefixedLog('log', `TBT approximation: ${report.tbt}ms (window: ${report.tbtWindow.start ?? 'start'} - ${report.tbtWindow.end})`);
  console.groupEnd();

  prefixedGroup('External scripts');
  console.table(report.externalScripts);
  console.groupEnd();

  prefixedGroup('CLS contributors');
  console.table(report.clsContributors);
  console.groupEnd();

  prefixedGroup('Frustration points');
  console.table(decodeStrings(report.frustrationPoints) as unknown[]);
  console.groupEnd();

  prefixedGroup('Storage / monitoring status');
  console.table(report.monitoringStatus);
  console.groupEnd();

  prefixedGroup('Limitations');
  prefixedLog('log', 'Script and long-task overlap is correlation, not confirmed causality.');
  prefixedLog('log', 'Exact Lighthouse TBT and Speed Index require a synthetic Lighthouse audit.');
  console.groupEnd();
  console.groupEnd();
}

async function persistPerformanceReport(phase: ReportPhase): Promise<void> {
  const report = buildSanitizedPerformanceReport();
  const frustrationCount = report.frustrationPoints.reduce((total, item) => total + item.events, 0);
  if (!shouldPersistReport(report.coreWebVitals, report.tbt, frustrationCount)) {
    return;
  }

  const timestamp = Date.now();
  const record = createPersistedReport(window.location.pathname, getVisitId(), phase, timestamp, report);

  try {
    const client = PerformanceReportClient.getInstance();
    await client.saveReport(record);
    return;
  } catch (error) {
    prefixedLog('warn', '[CWV] Worker persist failed, falling back to main-thread IndexedDB:', error);
  }

  if (phase === 'final') {
    void saveReportToMainIndexedDB(record).catch((error) => {
      prefixedLog('warn', '[CWV] Unable to persist final performance report:', error);
    });
  } else {
    scheduleMainThreadPersist(record);
  }
}

async function runStartupCleanup(): Promise<void> {
  try {
    const client = PerformanceReportClient.getInstance();
    await client.deleteExpired();
  } catch (error) {
    prefixedLog('warn', '[CWV] Unable to run startup expiration cleanup:', error);
  }
}

function persistFinalPerformanceReport(): void {
  if (isFinalReportPersisted) {
    return;
  }

  isFinalReportPersisted = true;
  void persistPerformanceReport('final')
    .then(() => {
      layoutShiftObserver?.disconnect();
      layoutShiftObserver = null;
      longTaskObserver?.disconnect();
      longTaskObserver = null;
      layoutShiftRecords.length = 0;
      PerformanceReportClient.getInstance().terminateWorker();
    })
    .catch((error) => {
      prefixedLog('warn', '[CWV] Unable to persist final performance report:', error);
    });
}

function logAndPersistInitialReport(): void {
  const report = buildSanitizedPerformanceReport();
  renderConsoleReport(report);
  void persistPerformanceReport('initial').catch((error) => {
    prefixedLog('warn', '[CWV] Unable to persist initial performance report:', error);
  });
}

/**
 * Get performance report with Core Web Vitals and other metrics
 */
export function getPerformanceReport(): string {
  const monitor = PerformanceMonitor.getInstance();
  const report = monitor.generateReport();
  const navData = getNavigationTimingData();

  let reportText = '';
  reportText += `DNS lookup: ${navData.dnsLookup}ms\n`;
  reportText += `TCP connection: ${navData.tcpConnection}ms\n`;
  reportText += `Request/Response: ${navData.requestResponse}ms\n`;
  reportText += `Response download: ${navData.responseDownload}ms\n`;
  reportText += `DOM processing: ${navData.domProcessing}ms\n`;
  reportText += `Page Load: ${navData.pageLoad}ms\n`;
  reportText += `Total page load: ${navData.totalTime}ms\n`;
  reportText += `LCP: ${report.lcp}\n`;
  reportText += `FID: ${report.fid}\n`;
  reportText += `CLS: ${report.cls}\n`;
  reportText += `TTFB: ${report.ttfb}\n`;

  const memoryInfo = getMemoryInfo();
  if (memoryInfo?.usedJSHeapSize) {
    reportText += `Memory Usage: ${Math.round(memoryInfo.usedJSHeapSize / (1024 * 1024))}MB\n`;
  }

  reportText += `Resource Timing\n`;

  const connectionInfo = getConnectionInfo();
  if (connectionInfo?.effectiveType) {
    reportText += `Connection Type: ${connectionInfo.effectiveType}`;
  }

  return reportText;
}

const normalizeLegacyMetrics = (raw: Record<string, unknown>): Record<string, unknown> => {
  const keyMap: Record<string, string> = {
    CLS: 'cls',
    FID: 'fid',
    FCP: 'fcp',
    LCP: 'lcp',
    TTFB: 'ttfb',
    CustomMetrics: 'customMetrics'
  };
  const normalized: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(raw)) {
    const targetKey = keyMap[key] ?? key.toLowerCase();
    if (typeof value === 'number') {
      normalized[targetKey] = value;
    } else if (typeof value === 'string') {
      const parsed = parseFloat(value);
      normalized[targetKey] = Number.isNaN(parsed) ? null : parsed;
    } else {
      normalized[targetKey] = value;
    }
  }

  return normalized;
};

const normalizeLegacyNavigation = (raw: Record<string, unknown>): Record<string, unknown> => {
  const keyMap: Record<string, string> = {
    dnsTime: 'dnsLookup',
    tcpTime: 'tcpConnection',
    requestTime: 'requestResponse',
    domTime: 'domProcessing',
    totalTime: 'totalTime'
  };
  const normalized: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(raw)) {
    normalized[keyMap[key] ?? key] = value;
  }

  return normalized;
};

function buildLegacyReports(): PerformanceReportRecord[] {
  const reports: PerformanceReportRecord[] = [];
  const now = Date.now();

  try {
    const rawMetrics = localStorage.getItem('globalPerformanceMetrics');
    if (rawMetrics) {
      const parsed = JSON.parse(rawMetrics);
      const entries = Array.isArray(parsed) ? parsed : [parsed];
      for (const entry of entries) {
        if (!entry?.pageName || !entry?.time) continue;
        const visitId = entry.visitId ?? getVisitId();
        const timestamp = new Date(entry.time).getTime() || now;
        const urlName = String(entry.pageName).split('?')[0].split('#')[0] || '/';
        const rawReport = {
          context: { page: urlName, collectedAt: new Date(timestamp).toISOString() },
          navigation: normalizeLegacyNavigation(entry.pageload ?? {}),
          coreWebVitals: normalizeLegacyMetrics(entry.performanceReport ?? {}),
          tbt: 0,
          tbtWindow: { start: null, end: timestamp },
          longTasks: [],
          externalScripts: [],
          clsContributors: [],
          frustrationPoints: [],
          monitoringStatus: {}
        };
        const origin = getGlobalWindow()?.location?.origin ?? '';
        const sanitized = sanitizePerformanceReport(rawReport, origin);
        reports.push(createPersistedReport(urlName, visitId, 'final', timestamp, sanitized));
      }
    }
  } catch (error) {
    prefixedLog('warn', '[CWV] Unable to read legacy performance metrics:', error);
  }

  return reports;
}

async function migrateLegacyStorage(): Promise<void> {
  try {
    const reports = buildLegacyReports();

    if (reports.length > 0) {
      const client = PerformanceReportClient.getInstance();
      const result = await client.migrateLegacy(reports);

      if (result.migrated === 0) {
        return;
      }
    }

    LEGACY_KEYS.forEach((key) => {
      try {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      } catch (error) {
        prefixedLog('warn', '[CWV] Unable to remove legacy storage key:', key, error);
      }
    });
  } catch (error) {
    prefixedLog('warn', '[CWV] Unable to migrate legacy performance data:', error);
  }
}

export async function getCwvReports(path?: string): Promise<PerformanceReportRecord[]> {
  const client = PerformanceReportClient.getInstance();
  const currentPath = getGlobalWindow()?.location?.pathname ?? '/';
  const urlName = path ?? currentPath;
  try {
    return await client.getReports({ urlName, limit: 50 });
  } catch (error) {
    prefixedLog('warn', '[CWV] Unable to retrieve performance reports:', error);
    return [];
  }
}

function logConsoleTableSection(label: string, items: unknown[], emptyMessage: string): void {
  if (isProduction) return;
  prefixedGroup(label);
  if (items.length > 0) {
    console.table(items);
  } else {
    prefixedLog('log', emptyMessage);
  }
  console.groupEnd();
}

const formatCwvMs = (value: number | null): string =>
  value !== null ? `${Math.round(value)}ms` : 'Not available';

const formatCwvCls = (value: number | null): string =>
  value !== null ? value.toFixed(3) : 'Not available';

export async function getPageInsights(pagename?: string): Promise<string> {
  if (isProduction) return 'Console insights are disabled in production';
  prefixedLog('log', '%c[Performance Insights]%c Beginning analysis...',
    'background:#3498db; color:white; font-weight: bold; padding: 0 8px', '');

  const currentPath = getGlobalWindow()?.location?.pathname ?? '/';
  const path = pagename ?? currentPath;
  const reports = await getCwvReports(path);

  if (reports.length === 0) {
    prefixedLog('log', `%c[Performance Insights]%c No reports found for path: "${path}"`,
      'background:#3498db; color:white; font-weight: bold; padding: 0 8px', '');
    return `No reports found for path: "${path}"`;
  }

  prefixedLog('log', `Found ${reports.length} report(s) for path: "${path}"`);
  reports.forEach((report, index) => {
    prefixedGroup(`Entry #${index + 1} - ${report.phase} - ${new Date(report.timestamp).toISOString()}`);

    prefixedGroup('Page Load Performance:');
    prefixedLog('log', `• DNS lookup: ${report.report.navigation.dnsLookup}ms`);
    prefixedLog('log', `• TCP connection: ${report.report.navigation.tcpConnection}ms`);
    prefixedLog('log', `• Request/Response: ${report.report.navigation.requestResponse}ms`);
    prefixedLog('log', `• Response download: ${report.report.navigation.responseDownload}ms`);
    prefixedLog('log', `• DOM processing: ${report.report.navigation.domProcessing}ms`);
    prefixedLog('log', `• Total page load: ${report.report.navigation.totalTime}ms`);
    console.groupEnd();

    prefixedGroup('Core Web Vitals:');
    prefixedLog('log', `• LCP (Largest Contentful Paint): ${formatCwvMs(report.report.coreWebVitals.lcp)}`);
    prefixedLog('log', `• FID (First Input Delay): ${formatCwvMs(report.report.coreWebVitals.fid)}`);
    prefixedLog('log', `• CLS (Cumulative Layout Shift): ${formatCwvCls(report.report.coreWebVitals.cls)}`);
    prefixedLog('log', `• TTFB (Time to First Byte): ${formatCwvMs(report.report.coreWebVitals.ttfb)}`);
    prefixedLog('log', `• FCP (First Contentful Paint): ${formatCwvMs(report.report.coreWebVitals.fcp)}`);
    console.groupEnd();

    prefixedGroup('Responsiveness / TBT diagnostics:');
    prefixedLog('log', `• TBT approximation: ${report.report.tbt}ms`);
    prefixedLog('log', `• TBT window: ${report.report.tbtWindow.start ?? 'start'} - ${report.report.tbtWindow.end}`);
    logConsoleTableSection('Long tasks', report.report.longTasks, 'No long tasks recorded.');
    console.groupEnd();

    logConsoleTableSection('External scripts', report.report.externalScripts, 'No external scripts recorded.');
    logConsoleTableSection('CLS contributors', report.report.clsContributors, 'No layout shifts recorded.');
    logConsoleTableSection('Frustration points', decodeStrings(report.report.frustrationPoints) as unknown[], 'No frustration points recorded.');

    prefixedGroup('Monitoring status:');
    console.table(report.report.monitoringStatus);
    console.groupEnd();

    console.groupEnd();
  });

  return `Successfully displayed metrics for ${path}`;
}

/**
 * Expose functions to global for console access
 */
export function exposeToWindow(): void {
  // Initialize window functions - will be properly set when the module loads
  if (typeof window !== 'undefined') {
    window.getPageInsights = getPageInsights;
    window.getCwvReports = getCwvReports;
    window.getPerformanceReport = getPerformanceReport;
    window.measureFunction = measureFunction;
    window.getFrustrationInsights = getFrustrationInsights;
  }
  exposeFrustrationToWindow();
}

/**
 * Get frustration insights and print them to console
 * Re-export from frustrationMonitor for consistent API
 */
export function getFrustrationInsights(): string {
  return getFrustrationData();
}
